import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCartIcon, HeartIcon, StarIcon, EyeIcon } from 'lucide-react';
import { Product } from '../../types';
import { formatPrice } from '../../utils/formatters';
import { useCart } from '../../context/CartContext';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface ProductCardProps {
  product: Product;
  onQuickView?: (product: Product) => void;
}
export function ProductCard({
  product,
  onQuickView
}: ProductCardProps) {
  const {
    addItem
  } = useCart();
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
  };
  return <div className="group bg-white rounded-xl border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden" data-id="element-716">
      {/* Image container */}
      <div className="relative aspect-square overflow-hidden bg-gray-50" data-id="element-717">
        <Link to={`/product/${product.slug}`} data-id="element-718">
          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-719" />
        </Link>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2" data-id="element-720">
          {product.discount && product.discount > 0 && <Badge variant="error" size="sm" data-id="element-721">
              -{product.discount}%
            </Badge>}
          {!product.inStock && <Badge variant="default" size="sm" data-id="element-722">
              Out of Stock
            </Badge>}
        </div>

        {/* Quick actions */}
        <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-723">
          <button className="w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-red-500 hover:bg-red-50 transition-colors" aria-label="Add to wishlist" data-id="element-724">
            <HeartIcon className="w-4 h-4" data-id="element-725" />
          </button>
          {onQuickView && <button onClick={e => {
          e.preventDefault();
          onQuickView(product);
        }} className="w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-primary-600 hover:bg-primary-50 transition-colors" aria-label="Quick view" data-id="element-726">
              <EyeIcon className="w-4 h-4" data-id="element-727" />
            </button>}
        </div>

        {/* Add to cart button */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-728">
          <Button variant="primary" size="sm" fullWidth leftIcon={<ShoppingCartIcon className="w-4 h-4" data-id="element-730" />} onClick={handleAddToCart} disabled={!product.inStock} data-id="element-729">
            Add to Cart
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4" data-id="element-731">
        <Link to={`/product/${product.slug}`} data-id="element-732">
          <p className="text-xs text-gray-500 mb-1" data-id="element-733">{product.brand}</p>
          <h3 className="font-medium text-gray-900 line-clamp-2 mb-2 hover:text-primary-600 transition-colors" data-id="element-734">
            {product.name}
          </h3>
        </Link>

        {/* Rating */}
        <div className="flex items-center gap-1 mb-2" data-id="element-735">
          <div className="flex items-center" data-id="element-736">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-3.5 h-3.5 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} data-id="element-737" />)}
          </div>
          <span className="text-xs text-gray-500" data-id="element-738">
            ({product.reviewCount.toLocaleString()})
          </span>
        </div>

        {/* Price */}
        <div className="flex items-center gap-2" data-id="element-739">
          <span className="text-lg font-bold text-gray-900" data-id="element-740">
            {formatPrice(product.price)}
          </span>
          {product.originalPrice && product.originalPrice > product.price && <span className="text-sm text-gray-400 line-through" data-id="element-741">
              {formatPrice(product.originalPrice)}
            </span>}
        </div>
      </div>
    </div>;
}